import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { apiGet } from "../api/client";

/**
 * Hours returned, by agent.
 *
 * The number a board will quote back, so the panel is built to survive the
 * question that follows it. Each bar is the sum of `ledger_entries` for that
 * agent; under it is the baseline the bar multiplied and who declared it. An
 * agent nobody has declared a baseline for is listed as "not measured", not
 * drawn as a zero bar — zero is an answer and "nobody has said" is not.
 *
 * No trend line, no target, no projection. When there is a month of entries
 * there will be a month to draw.
 */

interface Line {
  hand_key: string;
  decision_key: string;
  runs: number;
  touched: number;
  minutes_returned: number;
  baseline_minutes: number | null;
  declared_by_name: string | null;
  source_note: string | null;
}

interface DecisionRef {
  hand_key: string;
  hand_name: string;
  decision_key: string;
  decision_label: string;
}

interface LedgerResponse {
  lines: Line[];
  totals: { minutes_returned: number; runs: number };
  decisions: DecisionRef[];
}

function hours(minutes: number): string {
  const h = minutes / 60;
  return Math.abs(h) < 10 ? `${(Math.round(h * 10) / 10).toFixed(1)} h` : `${Math.round(h)} h`;
}

export function HoursByAgent({
  workspaceId,
  compact = false,
}: {
  workspaceId: string;
  /** On Home: the bars and one line of provenance, no per-agent detail. */
  compact?: boolean;
}) {
  const navigate = useNavigate();
  const q = useQuery<LedgerResponse, Error>({
    queryKey: ["ledger", workspaceId],
    enabled: !!workspaceId,
    queryFn: () => apiGet(`/api/workspaces/${workspaceId}/ledger`),
  });

  if (!q.data) return null;

  const names = new Map(q.data.decisions.map((d) => [d.hand_key, d.hand_name]));
  const byAgent = new Map<
    string,
    { minutes: number; runs: number; touched: number; measured: boolean; declaredBy: Set<string> }
  >();
  for (const d of q.data.decisions) {
    if (!byAgent.has(d.hand_key)) {
      byAgent.set(d.hand_key, { minutes: 0, runs: 0, touched: 0, measured: false, declaredBy: new Set() });
    }
  }
  for (const l of q.data.lines) {
    const a = byAgent.get(l.hand_key);
    if (!a) continue;
    a.minutes += l.minutes_returned;
    a.runs += l.runs;
    a.touched += l.touched;
    if (l.baseline_minutes !== null) {
      a.measured = true;
      if (l.declared_by_name) a.declaredBy.add(l.declared_by_name);
    }
  }

  const measured = [...byAgent.entries()]
    .filter(([, a]) => a.measured)
    .sort((x, y) => y[1].minutes - x[1].minutes);
  const unmeasured = [...byAgent.entries()].filter(([, a]) => !a.measured);
  const max = Math.max(1, ...measured.map(([, a]) => Math.abs(a.minutes)));
  const declarers = new Set(measured.flatMap(([, a]) => [...a.declaredBy]));

  if (measured.length === 0) {
    return (
      <div className="play-card" data-testid="hours-by-agent-empty">
        <div className="play-title">Hours returned are not measured yet.</div>
        <div className="play-meta">
          <span>
            Nobody has declared what any of this work used to take, so nothing
            is claimed. The supervisor declares baselines on the Efficiency
            Ledger.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="play-card" data-testid="hours-by-agent">
      <div className="play-head">
        <div>
          <div className="play-arch">Hours returned</div>
          {q.data.totals.runs === 0 ? (
            // Never a hero zero: say what makes the number move.
            <>
              <div className="play-title">No hours counted yet</div>
              <div className="play-meta">Hours returned counts once work is released: signed off by a person, or run on its own.</div>
            </>
          ) : (
            <div className="play-title">{hours(q.data.totals.minutes_returned)} across {q.data.totals.runs} runs</div>
          )}
        </div>
        <div className="play-status">
          <span
            className="v"
            role="link"
            onClick={() => navigate(`/workspaces/${workspaceId}/ledger`)}
            style={{ cursor: "pointer", color: "var(--accent)", fontSize: 12 }}
          >
            Efficiency Ledger →
          </span>
        </div>
      </div>

      <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
        {measured.map(([key, a]) => {
          const pct = (Math.abs(a.minutes) / max) * 100;
          const negative = a.minutes < 0;
          return (
            <div key={key} data-testid={`hours-${key}`}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                <span>{names.get(key) ?? key}</span>
                <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600, color: negative ? "var(--danger)" : "var(--text)" }}>
                  {negative ? "−" : ""}{hours(Math.abs(a.minutes))}
                </span>
              </div>
              <div
                aria-hidden="true"
                style={{ height: 8, background: "var(--bg-soft)", borderRadius: 4, marginTop: 4, overflow: "hidden" }}
              >
                <div
                  style={{
                    width: `${pct}%`,
                    height: "100%",
                    background: negative ? "var(--danger)" : "var(--accent)",
                    borderRadius: 4,
                    transition: "width .3s ease-out",
                  }}
                />
              </div>
              {!compact ? (
                <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 3 }}>
                  {a.runs} {a.runs === 1 ? "run" : "runs"} · {a.touched} touched by a person
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      {/* The provenance of every bar above, in one line. */}
      <div className="play-foot">
        <span>
          Baselines declared by {[...declarers].join(", ") || "—"}; each is on the ledger with its basis.
        </span>
        {unmeasured.length > 0 ? (
          <span>
            Not measured: {unmeasured.map(([k]) => names.get(k) ?? k).join(", ")}
          </span>
        ) : null}
      </div>
    </div>
  );
}
